import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Stack, Link as MuiLink, Alert, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { login, googleLogin } from '../services/authService';
import { trackSignupConversion } from '../utils/analytics';

// Helper function to detect input type (email or phone)
const detectInputType = (input: string): 'email' | 'phone' | 'unknown' => {
  const trimmed = input.trim();
  
  // Check for email pattern (contains @ and .)
  if (trimmed.includes('@')) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(trimmed) ? 'email' : 'unknown';
  }
  
  // Check for phone pattern (contains only digits, +, -, (, ), spaces)
  const phoneRegex = /^[\d\s\-+()]+$/;
  if (phoneRegex.test(trimmed)) {
    // Remove non-digits to count
    const digitsOnly = trimmed.replace(/\D/g, '');
    // Phone should have 10-15 digits
    if (digitsOnly.length >= 10 && digitsOnly.length <= 15) {
      return 'phone';
    }
  }
  
  return 'unknown';
};

const PageContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'grid',
  gridTemplateColumns: '1fr',
  '@media (min-width: 1024px)': {
    gridTemplateColumns: '1fr 1fr'
  }
}));

const LeftPanel = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2.5rem 1rem',
  backgroundColor: 'white',
  '@media (min-width: 640px)': {
    padding: '4rem 1.5rem'
  },
  '@media (min-width: 1024px)': {
    padding: '6rem 2rem'
  }
}));

const RightPanel = styled(Box)(() => ({
  display: 'none',
  '@media (min-width: 1024px)': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem 2rem',
    backgroundColor: '#F9FAFB'
  }
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    '& input': {
      padding: '16px'
    },
    '& fieldset': {
      borderColor: '#E5E7EB'
    },
    '&:hover fieldset': {
      borderColor: '#D1D5DB'
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
      '& fieldset': {
        borderColor: '#4F46E5',
        borderWidth: '1px'
  }
    }
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#9CA3AF',
    opacity: 1
  }
}));

const PrimaryButton = styled(Button)(() => ({
  width: '100%',
  padding: '16px',
  backgroundColor: '#4F46E5',
  color: 'white',
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#4338CA',
    boxShadow: 'none'
  },
  '&:disabled': {
    backgroundColor: '#D1D5DB',
    color: '#9CA3AF'
  }
}));

const SocialButton = styled(Button)(() => ({
  position: 'relative',
  width: '100%',
  padding: '16px',
  backgroundColor: 'white',
  border: '2px solid #E5E7EB',
  borderRadius: '8px',
  color: '#374151',
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  justifyContent: 'center',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#F9FAFB',
    color: '#111827',
    boxShadow: 'none'
  }
}));

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Sign In form state - single input for email or phone
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [
    {
      image: 'https://cdn.rareblocks.xyz/collection/celebration/images/signup/1/cards.png',
      title: 'Boost your business reputation',
      description: 'Manage all your reviews in one place. Get more positive feedback and grow your business with our powerful review management platform.'
    },
    {
      image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&auto=format&fit=crop',
      title: 'Automate review requests',
      description: 'Send automated review requests to your customers and increase your positive feedback with smart timing and personalized messages.'
    },
    {
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop',
      title: 'Track your performance',
      description: 'Monitor your online reputation with real-time analytics and insights. See what customers are saying across all platforms.'
    }
  ];

  // Show success message from navigation state (e.g., from password reset)
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, [slides.length]);

  const handleSignInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Validate input
      if (!emailOrPhone) {
        setError('Please enter your email address or phone number');
        setIsLoading(false);
        return;
      }

      // Auto-detect input type
      const inputType = detectInputType(emailOrPhone);
      
      if (inputType === 'unknown') {
        setError('Please enter a valid email address or phone number');
        setIsLoading(false);
        return;
      }

      // Validate country code for phone numbers
      if (inputType === 'phone') {
        const trimmedPhone = emailOrPhone.trim();
        if (!trimmedPhone.startsWith('+')) {
          setError('Phone number must include country code (e.g., +1 for US, +91 for India)');
          setIsLoading(false);
          return;
        }
        
        // Validate that country code has at least 1 digit after +
        const digitsAfterPlus = trimmedPhone.substring(1).replace(/\D/g, '');
        if (digitsAfterPlus.length < 10) {
          setError('Please enter a complete phone number with country code (e.g., +1234567890)');
          setIsLoading(false);
          return;
        }
      }

      if (!password) {
        setError('Please enter your password');
        setIsLoading(false);
        return;
      }

      console.log('Signing in with:', inputType, emailOrPhone);

      // Call login API with detected type
      const response = await login({
        ...(inputType === 'email' 
          ? { email: emailOrPhone } 
          : { phone: emailOrPhone }
        ),
        password: password
      });

      console.log('Login successful:', response);

      setSuccess('Login successful! Redirecting to dashboard...');

      // Navigate to dashboard after successful sign in
      navigate('/dashboard');

    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLoginClick = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('Google OAuth success, token received');
        
        // Send with 'login' intent
        const result = await googleLogin(tokenResponse.access_token, 'login');
        
        if (result.success && result.data) {
          console.log('✅ Google login successful');
          
          // Check if profile creation is required
          if (result.data.requires_profile_creation || result.data.is_first_time_user) {
            console.log('→ New user or incomplete profile, redirecting to complete profile');
            navigate('/complete-profile');
          } else {
            console.log('→ Existing user with complete profile, redirecting to dashboard');
            navigate('/dashboard');
          }
        } else {
          setError(result.message || 'Google login failed. Please try again.');
        }
      } catch (err) {
        console.error('Google login error:', err);
        setError(err instanceof Error ? err.message : 'Google login failed');
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      setError('Google login was unsuccessful. Please try again.');
    },
  });

  return (
    <PageContainer>
      <LeftPanel>
        <Box sx={{ width: '100%', maxWidth: 448, mx: 'auto' }}>
          {/* Header */}
          <Typography
            sx={{
              fontSize: { xs: '1.875rem', sm: '2.25rem' },
              fontWeight: 700,
              lineHeight: 1.2,
              color: '#111827',
              mb: 1
            }}
          >
            Sign in to RiteReview
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: '#6B7280', mb: 4 }}>
            Don't have an account?{' '}
            <MuiLink
              href="/signup"
              onClick={(e) => {
                e.preventDefault();
                // Track signup conversion before navigating
                trackSignupConversion(navigate, '/signup');
              }}
              sx={{
                color: '#4F46E5',
                fontWeight: 500,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#4338CA'
                }
              }}
            >
              Create a free account
            </MuiLink>
          </Typography>

          {/* Alerts */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Form */}
            <Box component="form" onSubmit={handleSignInSubmit}>
            <Stack spacing={2.5}>
              {/* Email or Phone Field */}
                <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#111827',
                    mb: 1.25
                  }}
                >
                  Email or Phone
                  </Typography>
                  <StyledTextField
                    fullWidth
                    type="text"
                    placeholder="Enter email or phone (with country code, e.g., +1234567890)"
                    value={emailOrPhone}
                    onChange={(e) => setEmailOrPhone(e.target.value)}
                    required
                  />
                </Box>

              {/* Password Field */}
                <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                  <Typography
                    component="label"
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: '#111827'
                    }}
                  >
                      Password
                    </Typography>
                    <MuiLink
                    href="/forgot-password"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/forgot-password');
                    }}
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      color: '#4F46E5',
                        textDecoration: 'none',
                        '&:hover': {
                        textDecoration: 'underline',
                        color: '#4338CA'
                      }
                    }}
                  >
                    Forgot password?
                  </MuiLink>
                </Box>
                  <StyledTextField
                    fullWidth
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  />
                </Box>

              {/* Submit Button */}
              <PrimaryButton type="submit" disabled={isLoading}>
                  {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                  ) : (
                  'Log in'
                  )}
                </PrimaryButton>
            </Stack>
                        </Box>

          {/* Social Sign In */}
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <SocialButton onClick={() => handleGoogleLoginClick()} disabled={isLoading}>
                  <Box
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#EA4335' }}>
                  <path d="M20.283 10.356h-8.327v3.451h4.792c-.446 2.193-2.313 3.453-4.792 3.453a5.27 5.27 0 0 1-5.279-5.28 5.27 5.27 0 0 1 5.279-5.279c1.259 0 2.397.447 3.29 1.178l2.6-2.599c-1.584-1.381-3.615-2.233-5.89-2.233a8.908 8.908 0 0 0-8.934 8.934 8.907 8.907 0 0 0 8.934 8.934c4.467 0 8.529-3.249 8.529-8.934 0-.528-.081-1.097-.202-1.625z" />
                </svg>
              </Box>
              Sign in with Google
                </SocialButton>

                <SocialButton>
                  <Box
                  sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#1877F2' }}>
                  <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z" />
                </svg>
              </Box>
              Sign in with Facebook
            </SocialButton>
              </Stack>
        </Box>
      </LeftPanel>

      <RightPanel>
        <Box sx={{ position: 'relative' }}>
          {/* Carousel Images */}
        <Box
          sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '12px',
              mb: 4
            }}
          >
            {slides.map((slide, index) => (
              <Box
                key={index}
                sx={{
                  display: currentSlide === index ? 'block' : 'none',
                  transition: 'opacity 0.5s ease-in-out',
                  opacity: currentSlide === index ? 1 : 0
                }}
              >
                <Box
                  component="img"
                  src={slide.image}
                  alt={slide.title}
                  sx={{
                    width: '100%',
                    maxWidth: 600,
                    height: 'auto',
                    mx: 'auto',
                    display: 'block',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* Carousel Content */}
          <Box sx={{ maxWidth: 672, mx: 'auto', textAlign: 'center' }}>
          <Typography
            sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#111827',
                mb: 1.25,
                transition: 'opacity 0.3s ease-in-out',
                minHeight: '2rem'
            }}
          >
              {slides[currentSlide].title}
          </Typography>
          <Typography
            sx={{
                fontSize: '1rem',
              lineHeight: 1.6,
                color: '#6B7280',
                mb: 5,
                transition: 'opacity 0.3s ease-in-out',
                minHeight: '3rem'
            }}
          >
              {slides[currentSlide].description}
          </Typography>

            {/* Carousel Indicators */}
            <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center">
              {slides.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  sx={{
                    width: currentSlide === index ? 80 : 48,
                    height: 6,
                    borderRadius: '999px',
                    bgcolor: currentSlide === index ? '#F97316' : '#E5E7EB',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: currentSlide === index ? '#F97316' : '#D1D5DB'
                    }
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      </RightPanel>
    </PageContainer>
  );
};

export default SignIn;